import { create } from "zustand";
import { broadcastSync } from "@/lib/sync-broadcast";
import {
  hydrateStateDoc,
  initialSyncState,
  persistStateDoc,
  pushStateDoc,
  syncStateDoc,
  type StateDocSyncSlice,
} from "@/lib/bff-state-doc";

/**
 * Previous-workshop summaries store (Track A · CC-SUMMARIES). Exactly **one
 * summary per workshop** — a short narrative a facilitator writes to carry
 * continuity forward ("where WS3 left us"). Keyed by workshopId (an upsert,
 * not an append log), so it's a small hand-written store rather than a
 * `createWorkshopRecordStore` instance.
 *
 * Persistence — the same dual backend as the record stores (see
 * `@/lib/bff-state-doc`): IndexedDB only by default (`xaes-workshop-summaries`),
 * or a shared JSON blob at `workshops/_program/summaries.json` when
 * `VITE_WORKSHOP_BACKEND=bff`. A continuity summary is the headline artefact of
 * the "carried forward" block, so it must be shared across the team — the
 * single most visible continuity content can't be stranded in one browser. On
 * the BFF backend the store hydrates from the shared copy, writes through
 * optimistically, and reconciles on the 15s / focus / cross-tab poll
 * (registered in `SYNCED_STORES`). Reconcile is per-workshop last-write-wins by
 * each summary's `updatedAt`, so two facilitators editing *different*
 * workshops' summaries never clobber each other, and a poll mid-edit can't
 * revert a just-saved local change.
 *
 * `source` distinguishes a `'manual'` summary from a later `'ai'` fill (the
 * 🚀 `AI-SUMMARIES` task) — the AI fill is always an editable draft a human
 * Saves, never auto-committed.
 */

export type SummarySource = "manual" | "ai";

export type WorkshopSummary = {
  workshopId: string;
  body: string;
  updatedAt: number;
  updatedBy: string;
  source: SummarySource;
};

const IDB_KEY = "xaes-workshop-summaries";
const BFF_PATH = "_program/summaries.json";
const SOURCES: SummarySource[] = ["manual", "ai"];

type SummariesStore = StateDocSyncSlice & {
  /** workshopId → summary. Absent key = no summary written yet. */
  byWorkshop: Record<string, WorkshopSummary>;
  isHydrated: boolean;
  /** Set when a BFF hydrate couldn't reach the service (cached copy shown). */
  loadError: string | null;
  hydrate: () => Promise<void>;
  /** Upsert the summary for a workshop. */
  set: (summary: WorkshopSummary) => void;
  /** Clear a workshop's summary. */
  clear: (workshopId: string) => void;
  /** One-time cutover push of the local summaries to the shared backend
   *  (IDB→BFF migration). Returns the number of summaries pushed, 0 when the
   *  BFF backend is disabled. */
  pushLocalToBff: () => Promise<number>;
};

function sanitise(raw: unknown): Record<string, WorkshopSummary> {
  const out: Record<string, WorkshopSummary> = {};
  if (typeof raw !== "object" || raw === null) return out;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== "object" || value === null) continue;
    const s = value as Record<string, unknown>;
    if (typeof s.body !== "string") continue;
    const workshopId = typeof s.workshopId === "string" ? s.workshopId : key;
    const source: SummarySource = SOURCES.includes(s.source as SummarySource)
      ? (s.source as SummarySource)
      : "manual";
    out[workshopId] = {
      workshopId,
      body: s.body,
      updatedAt: typeof s.updatedAt === "number" ? s.updatedAt : Date.now(),
      updatedBy: typeof s.updatedBy === "string" ? s.updatedBy : "",
      source,
    };
  }
  return out;
}

/** Per-workshop recency reconcile: keep whichever side's summary is newer for
 *  each workshop. Shared-only workshops come through; local-only stay; a tie
 *  keeps local (protects a fresh local edit from a stale poll). */
function mergeSummaries(
  remote: Record<string, WorkshopSummary> | null,
  local: Record<string, WorkshopSummary>,
): Record<string, WorkshopSummary> {
  if (!remote) return local;
  const out: Record<string, WorkshopSummary> = { ...remote };
  for (const [wsId, l] of Object.entries(local)) {
    const r = remote[wsId];
    if (!r || l.updatedAt >= r.updatedAt) out[wsId] = l;
  }
  return out;
}

export const useWorkshopSummaries = create<SummariesStore>((set, get) => ({
  byWorkshop: {},
  isHydrated: false,
  loadError: null,
  syncState: initialSyncState(),
  lastSyncedAt: null,
  syncError: null,
  hydrate: async () => {
    if (get().isHydrated) return;
    const { value, loadError } = await hydrateStateDoc({
      idbKey: IDB_KEY,
      path: BFF_PATH,
      parse: sanitise,
      merge: mergeSummaries,
    });
    set({ byWorkshop: value, isHydrated: true, loadError });
  },
  sync: async () => {
    if (get().syncState === "disabled") return;
    set({ syncState: "syncing", syncError: null });
    const { value, state, error } = await syncStateDoc({
      idbKey: IDB_KEY,
      path: BFF_PATH,
      parse: sanitise,
      merge: mergeSummaries,
      local: get().byWorkshop,
    });
    set({
      byWorkshop: value,
      isHydrated: true,
      syncState: state,
      syncError: error,
      lastSyncedAt: state === "synced" ? Date.now() : get().lastSyncedAt,
    });
  },
  set: (summary) => {
    const next = { ...get().byWorkshop, [summary.workshopId]: summary };
    set({ byWorkshop: next });
    persistStateDoc({
      idbKey: IDB_KEY,
      path: BFF_PATH,
      value: next,
      parse: sanitise,
      merge: mergeSummaries,
      onReconciled: (byWorkshop) => set({ byWorkshop }),
    });
    broadcastSync();
  },
  clear: (workshopId) => {
    const next = { ...get().byWorkshop };
    delete next[workshopId];
    set({ byWorkshop: next });
    persistStateDoc({
      idbKey: IDB_KEY,
      path: BFF_PATH,
      value: next,
      parse: sanitise,
      merge: mergeSummaries,
      onReconciled: (byWorkshop) => set({ byWorkshop }),
    });
    broadcastSync();
  },
  pushLocalToBff: async () => {
    const byWorkshop = get().byWorkshop;
    const pushed = await pushStateDoc(BFF_PATH, byWorkshop, sanitise, mergeSummaries);
    return pushed ? Object.keys(byWorkshop).length : 0;
  },
}));
